/**
 * Duration Distribution Utils
 *
 * Pure functions for automatic duration balancing in slider items.
 * Adapted from placements/utils/weightDistribution.ts for A/B test variants.
 *
 * Features:
 * - Sum always equals 100%
 * - Minimum duration per slide = 5%
 * - Range-based editing (shared boundaries between adjacent slides)
 */

import type { DurationRange } from '../types/sliderSection.types';

/** Minimum duration percentage per slide (5%) */
export const MIN_DURATION_PERCENT = 5;

/** Total percentage that must be distributed (100%) */
export const TOTAL_PERCENT = 100;

/** Maximum number of slides allowed */
export const MAX_SLIDES = 10;

/** Minimum number of slides required */
export const MIN_SLIDES = 1;

/**
 * Distribute durations evenly among N slides.
 * Extra percentage from rounding goes to first slides.
 *
 * @param count - Number of slides
 * @returns Array of durations that sum to 100
 *
 * @example
 * distributeDurationsEvenly(2) // [50, 50]
 * distributeDurationsEvenly(3) // [34, 33, 33]
 * distributeDurationsEvenly(4) // [25, 25, 25, 25]
 */
export function distributeDurationsEvenly(count: number): number[] {
  if (count <= 0) return [];
  if (count === 1) return [TOTAL_PERCENT];

  const baseDuration = Math.floor(TOTAL_PERCENT / count);
  const remainder = TOTAL_PERCENT - baseDuration * count;

  return Array.from({ length: count }, (_, i) =>
    baseDuration + (i < remainder ? 1 : 0)
  );
}

/**
 * Calculate range boundaries from duration percentages.
 * Returns array of [start, end] for each slide.
 *
 * @param durations - Array of duration percentages
 * @returns Array of [start, end] ranges
 *
 * @example
 * calculateRanges([50, 50]) // [[0, 50], [50, 100]]
 * calculateRanges([25, 25, 50]) // [[0, 25], [25, 50], [50, 100]]
 */
export function calculateRanges(durations: number[]): DurationRange[] {
  const ranges: DurationRange[] = [];
  let currentStart = 0;

  for (const duration of durations) {
    const end = currentStart + duration;
    ranges.push([currentStart, end]);
    currentStart = end;
  }

  return ranges;
}

/**
 * Calculate ranges from items with durationPercent property.
 *
 * @param items - Array of items with durationPercent
 * @returns Array of [start, end] ranges
 */
export function calculateRangesFromItems(
  items: Array<{ durationPercent: number }>
): DurationRange[] {
  return calculateRanges(items.map((item) => item.durationPercent));
}

/**
 * Handle range slider change.
 * Updates durations when a boundary between slides is moved.
 *
 * @param durations - Current duration percentages
 * @param index - Index of the slide being changed
 * @param newRange - New [start, end] range from slider
 * @returns Updated duration percentages
 */
export function handleRangeChange(
  durations: number[],
  index: number,
  newRange: [number, number]
): number[] {
  const count = durations.length;
  if (count === 0) return [];
  if (count === 1) return [TOTAL_PERCENT];

  const [newStart, newEnd] = newRange;
  const ranges = calculateRanges(durations);
  const currentRange = ranges[index];
  const isFirst = index === 0;
  const isLast = index === count - 1;

  // Create new durations array
  const newDurations = [...durations];

  // Determine what changed
  const startChanged = newStart !== currentRange[0];
  const endChanged = newEnd !== currentRange[1];

  if (startChanged && !isFirst) {
    // Left thumb moved - affects previous slide's end
    const prevIndex = index - 1;
    const prevStart = ranges[prevIndex][0];

    // Ensure minimum duration for both slides
    const clampedStart = Math.max(
      prevStart + MIN_DURATION_PERCENT,
      Math.min(newStart, currentRange[1] - MIN_DURATION_PERCENT)
    );

    newDurations[prevIndex] = clampedStart - prevStart;
    newDurations[index] = currentRange[1] - clampedStart;
  }

  if (endChanged && !isLast) {
    // Right thumb moved - affects next slide's start
    const nextIndex = index + 1;
    const nextEnd = ranges[nextIndex][1];

    // Ensure minimum duration for both slides
    const clampedEnd = Math.max(
      currentRange[0] + MIN_DURATION_PERCENT,
      Math.min(newEnd, nextEnd - MIN_DURATION_PERCENT)
    );

    newDurations[index] = clampedEnd - currentRange[0];
    newDurations[nextIndex] = nextEnd - clampedEnd;
  }

  return newDurations;
}

/**
 * Redistribute durations when adding a new slide.
 * Evenly distributes among all slides including the new one.
 *
 * @param currentDurations - Current duration percentages
 * @returns New duration percentages including the added slide
 */
export function redistributeAfterAdd(currentDurations: number[]): number[] {
  return distributeDurationsEvenly(currentDurations.length + 1);
}

/**
 * Redistribute durations when removing a slide.
 * Evenly distributes among remaining slides.
 *
 * @param currentDurations - Current duration percentages
 * @param removedIndex - Index of the removed slide
 * @returns New duration percentages for remaining slides
 */
export function redistributeAfterRemove(
  currentDurations: number[],
  removedIndex: number
): number[] {
  const remaining = currentDurations.filter((_, i) => i !== removedIndex);
  if (remaining.length === 0) return [];
  return distributeDurationsEvenly(remaining.length);
}

/**
 * Redistribute durations when one slide's duration is changed.
 * Adjusts other slides proportionally to maintain 100% total.
 *
 * @param currentDurations - Current duration percentages
 * @param changedIndex - Index of the slide whose duration was changed
 * @param newPercent - New duration percentage for the changed slide
 * @returns New duration percentages that sum to 100
 *
 * @example
 * redistributeAfterChange([50, 50], 0, 60) // [60, 40]
 * redistributeAfterChange([33, 33, 34], 1, 50) // [25, 50, 25]
 */
export function redistributeAfterChange(
  currentDurations: number[],
  changedIndex: number,
  newPercent: number
): number[] {
  const count = currentDurations.length;
  if (count === 0) return [];
  if (count === 1) return [TOTAL_PERCENT];

  // Clamp newPercent to valid range
  const maxPercent = getMaxAllowedDuration(count, changedIndex);
  const clampedPercent = Math.max(MIN_DURATION_PERCENT, Math.min(maxPercent, newPercent));

  // Calculate remaining percentage to distribute among other items
  const remainingPercent = TOTAL_PERCENT - clampedPercent;
  const othersCount = count - 1;

  // Get total of other items for proportional distribution
  const othersDurations = currentDurations.filter((_, i) => i !== changedIndex);
  const othersTotal = othersDurations.reduce((sum, d) => sum + d, 0);

  // Build new durations array
  const newDurations: number[] = [];
  let distributedSum = 0;

  for (let i = 0; i < count; i++) {
    if (i === changedIndex) {
      newDurations.push(clampedPercent);
    } else {
      // Distribute proportionally, but ensure minimum
      const originalPercent = currentDurations[i];
      let proportionalPercent: number;

      if (othersTotal > 0) {
        proportionalPercent = Math.round((originalPercent / othersTotal) * remainingPercent);
      } else {
        proportionalPercent = Math.round(remainingPercent / othersCount);
      }

      // Ensure minimum
      const ensuredPercent = Math.max(MIN_DURATION_PERCENT, proportionalPercent);
      newDurations.push(ensuredPercent);
      distributedSum += ensuredPercent;
    }
  }

  // Adjust for rounding errors - ensure total is exactly 100
  const total = newDurations.reduce((sum, d) => sum + d, 0);
  if (total !== TOTAL_PERCENT) {
    // Find a non-changed item to adjust
    const adjustIndex = changedIndex === count - 1 ? 0 : count - 1;
    if (adjustIndex !== changedIndex) {
      newDurations[adjustIndex] += TOTAL_PERCENT - total;
      // Ensure it doesn't go below minimum
      if (newDurations[adjustIndex] < MIN_DURATION_PERCENT) {
        // Redistribute the negative amount
        const deficit = MIN_DURATION_PERCENT - newDurations[adjustIndex];
        newDurations[adjustIndex] = MIN_DURATION_PERCENT;
        // Take from the changed item
        newDurations[changedIndex] = Math.max(
          MIN_DURATION_PERCENT,
          newDurations[changedIndex] - deficit
        );
      }
    }
  }

  return newDurations;
}

/**
 * Calculate actual durations in milliseconds from percentages.
 *
 * @param totalDurationMs - Total duration in milliseconds
 * @param percentages - Duration percentages
 * @returns Actual durations in milliseconds
 *
 * @example
 * calculateActualDurations(15000, [33, 33, 34]) // [4950, 4950, 5100]
 */
export function calculateActualDurations(
  totalDurationMs: number,
  percentages: number[]
): number[] {
  return percentages.map((percent) =>
    Math.round((totalDurationMs * percent) / TOTAL_PERCENT)
  );
}

/**
 * Calculate actual durations in seconds from percentages.
 *
 * @param totalDurationSeconds - Total duration in seconds
 * @param percentages - Duration percentages
 * @returns Actual durations in seconds
 */
export function calculateActualDurationsSeconds(
  totalDurationSeconds: number,
  percentages: number[]
): number[] {
  return percentages.map((percent) =>
    (totalDurationSeconds * percent) / TOTAL_PERCENT
  );
}

/**
 * Get maximum allowed duration for a slide.
 * Ensures other slides can have at least MIN_DURATION_PERCENT each.
 *
 * @param count - Total number of slides
 * @param index - Index of the slide
 * @returns Maximum duration percentage this slide can have
 */
export function getMaxAllowedDuration(count: number, index: number): number {
  if (count <= 1) return TOTAL_PERCENT;

  const othersCount = count - 1;
  const minReserved = othersCount * MIN_DURATION_PERCENT;

  return TOTAL_PERCENT - minReserved;
}

/**
 * Validate that durations sum to 100%.
 *
 * @param durations - Duration percentages to validate
 * @returns true if valid, false otherwise
 */
export function validateDurations(durations: number[]): boolean {
  if (durations.length === 0) return true;

  const sum = durations.reduce((acc, d) => acc + d, 0);
  return Math.abs(sum - TOTAL_PERCENT) < 0.01; // Allow tiny rounding errors
}

/**
 * Normalize durations to ensure they sum to exactly 100%.
 * Adjusts the last item to compensate for rounding.
 *
 * @param durations - Duration percentages
 * @returns Normalized durations that sum to exactly 100
 */
export function normalizeDurations(durations: number[]): number[] {
  if (durations.length === 0) return [];
  if (durations.length === 1) return [TOTAL_PERCENT];

  const sum = durations.reduce((acc, d) => acc + d, 0);
  const diff = TOTAL_PERCENT - sum;

  // Adjust the last item
  const normalized = [...durations];
  normalized[normalized.length - 1] += diff;

  return normalized;
}

/**
 * Check if more slides can be added
 */
export function canAddSlide(currentCount: number): boolean {
  return currentCount < MAX_SLIDES;
}

/**
 * Check if a slide can be removed
 */
export function canRemoveSlide(currentCount: number): boolean {
  return currentCount > MIN_SLIDES;
}
